'use client'

import React, { Suspense, useRef, useState } from 'react'
import posthog from 'posthog-js'
import { useSearchParams } from 'next/navigation'
import Image from 'next/image'
import LogoImage from '@/graphics/Logo/emda_logo.png'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { AlertCircle } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import Link from 'next/link'
import { useAuth } from '@/providers/auth-provider'
import TurnstileCaptcha, {
  TurnstileStatus,
  TurnstileCaptchaRef,
} from '@/components/TurnstileCaptcha'
import { getWithErrorHandling } from '@/app/(frontend)/helpers/fetch-helpers'
import { TwoFactorSetup } from '@/components/TwoFactorSetup'
import { getSafeCallbackUrl } from '@/utils/safeCallbackUrl'

// Client component that uses useSearchParams
function SignInContent() {
  const searchParams = useSearchParams()
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    totpCode: '',
  })
  const [error, setError] = useState('')
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [turnstileStatus, setTurnstileStatus] = useState<TurnstileStatus>('required')
  const [requiresTwoFactor, setRequiresTwoFactor] = useState(false)
  const [requiresSetup, setRequiresSetup] = useState(false)
  const turnstileRef = useRef<TurnstileCaptchaRef>(null)
  const { login, setUser } = useAuth()

  // Helper function to get callback URL from current URL search params.
  // Validates that the URL is same-origin to prevent open redirect attacks.
  const getCallbackUrl = () => {
    const raw =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('callbackUrl')
        : searchParams.get('callbackUrl')
    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    return getSafeCallbackUrl(raw, origin)
  }

  // Helper function to reset Turnstile after errors.
  //
  // The token is single-use, so a retry needs a fresh one or the submit button stays disabled.
  // Clear the burned token *before* re-arming: the widget can report the new token synchronously,
  // and clearing afterwards would discard it. `reset()` remounts only the underlying widget, so
  // unlike a `key` bump on TurnstileCaptcha it does not re-run that component's /api/config fetch
  // — a fetch whose failure would leave this form with no widget and no way back. See #3973.
  const resetTurnstile = () => {
    setTurnstileStatus('required')
    setTurnstileToken(null)
    turnstileRef.current?.reset()
  }

  const handleTurnstileVerify = (token: string) => {
    setTurnstileToken(token)
    setTurnstileStatus('success')
  }

  const handleTurnstileError = () => {
    setTurnstileStatus('error')
    setTurnstileToken(null)
  }

  const handleTurnstileExpire = () => {
    setTurnstileStatus('expired')
    setTurnstileToken(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Only verify Turnstile on first login attempt, not when submitting 2FA code
    if (!requiresTwoFactor) {
      if (turnstileStatus !== 'success' || !turnstileToken) {
        setError('אנא אמת שאינך רובוט')
        return
      }
    }

    setIsLoading(true)
    setError('')

    // Identify the user in PostHog BEFORE the login request resolves so failed
    // logins (including 2FA failures) are attributed to a real person keyed by
    // email. We intentionally use the raw normalized email as distinct_id so the
    // email is visible directly on the PostHog person/session — required for
    // mass onboarding visibility. Do NOT hash it.
    const normalizedEmail = formData.email ? formData.email.toLowerCase().trim() : ''
    if (normalizedEmail) {
      posthog.identify(normalizedEmail, { email: normalizedEmail })
      posthog.capture('login_attempt', {
        email: normalizedEmail,
        step: requiresTwoFactor ? '2fa' : 'password',
        surface: 'user',
      })
    }

    try {
      // Attempt login. On the first (password) factor the Turnstile token is sent with
      // the request and verified server-side in the Users `beforeOperation` login hook — the check
      // must live on the endpoint, not here, so /api/users/login cannot be called directly
      // without it. The token is single-use, so it is verified in exactly one place (login).
      // The 2FA step reuses the human already proven at step 1, so no token is sent then.
      const user = await login({
        email: formData.email,
        password: formData.password,
        totpCode: requiresTwoFactor ? formData.totpCode : undefined,
        turnstileToken: !requiresTwoFactor ? (turnstileToken ?? undefined) : undefined,
      })
      // Verify user object was returned
      if (!user) {
        throw new Error('פרטי ההתחברות שגויים')
      }
      // Check if user has incomplete 2FA setup after successful login
      if (user && user.twoFactorAuth?.enabled && !user.twoFactorAuth?.verified) {
        console.log('User has incomplete 2FA setup, showing setup interface')
        setRequiresSetup(true)
        setError('')
        return
      }

      // Login successful — capture sign-in events and alias the email-keyed
      // pre-login person into the real user.id person so the funnel events
      // captured before auth succeeded aren't fragmented.
      if (normalizedEmail && user?.id) {
        posthog.alias(user.id, normalizedEmail)
      }
      posthog.capture('login_success', {
        email: normalizedEmail,
        step: requiresTwoFactor ? '2fa' : 'password',
        surface: 'user',
      })
      // Kept for backwards-compatibility with existing dashboards/funnels.
      posthog.capture('user_signed_in', {
        method: requiresTwoFactor ? '2fa' : 'password',
        surface: 'user',
      })

      // Redirect to callback URL
      // Use window.location.href for reliable navigation after page refresh
      const callbackUrl = getCallbackUrl()
      window.location.href = callbackUrl
    } catch (error: any) {
      // Check if this is a 2FA required case (which is a legitimate flow, not an error)
      if (error.requires2FA) {
        console.log('2FA required detected, showing TOTP input')
        setRequiresTwoFactor(true)
        setError('')
      } else {
        console.log('Actual login error:', error)
        setError(error instanceof Error ? error.message : 'שגיאה בכניסה למערכת')
        if (normalizedEmail) {
          posthog.capture('login_failed', {
            email: normalizedEmail,
            reason: error?.code || error?.reason || 'invalid_credentials',
            step: requiresTwoFactor ? '2fa' : 'password',
            surface: 'user',
          })
        }
        // Reset Turnstile on login errors so user can try again without refreshing page
        if (!requiresTwoFactor) {
          resetTurnstile()
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Show 2FA setup message if incomplete setup detected
  if (requiresSetup) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-8rem)] py-8 px-4">
        <div className="w-full max-w-md">
          <Card className="mb-4">
            <CardHeader>
              <div className="flex justify-center mb-4">
                <Image
                  src={LogoImage}
                  alt="Emda Logo"
                  width={150}
                  height={150}
                  style={{
                    width: 'auto',
                    height: 'auto',
                  }}
                  priority
                />
              </div>
              <CardTitle className="text-center text-red-600">אימות דו-שלבי נדרש</CardTitle>
              <CardDescription className="text-center">
                למטרות אבטחה, עליך להגדיר אימות דו-שלבי כדי להמשיך
              </CardDescription>
            </CardHeader>
          </Card>

          <TwoFactorSetup
            onComplete={async () => {
              try {
                // Fetch updated user data after successful 2FA setup
                const updatedUserResponse = await getWithErrorHandling('/api/users/me', {
                  notifyOnError: false,
                })

                if (updatedUserResponse?.user) {
                  // Update the user in auth context with the fresh data
                  setUser(updatedUserResponse.user)
                  // Capture 2FA setup completion (identify is handled by PostHogIdentifier after redirect)
                  posthog.capture('user_signed_in', {
                    method: '2fa_setup_completed',
                    surface: 'user',
                  })
                }

                // After successful setup, update state and redirect
                setRequiresSetup(false)
                window.location.href = getCallbackUrl()
              } catch (_error) {
                // If fetching updated user fails, still proceed with redirect
                setRequiresSetup(false)
                window.location.href = getCallbackUrl()
              }
            }}
            onCancel={undefined} // No cancel option - setup is mandatory
          />
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-8rem)] py-8 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4">
          <div className="flex justify-center">
            <Image
              src={LogoImage}
              alt="Emda Logo"
              width={87}
              height={60}
              style={{ width: 'auto', height: 'auto' }}
              priority
            />
          </div>
          <CardTitle className="text-2xl font-bold text-center">התחברות</CardTitle>
          <CardDescription className="text-center">
            {requiresTwoFactor
              ? 'הזן את קוד האימות הדו-שלבי כדי להשלים את ההתחברות'
              : 'הזן את פרטי ההתחברות שלך כדי להיכנס למערכת'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">אימייל</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">סיסמה</Label>
                <Link href="/auth/reset-password" className="text-sm text-primary hover:underline">
                  שכחת סיסמה?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                disabled={isLoading}
              />
            </div>
            {requiresTwoFactor && (
              <div className="space-y-2">
                <Label htmlFor="totpCode">קוד אימות דו שלבי</Label>
                <Input
                  id="totpCode"
                  type="text"
                  placeholder="הזן את קוד האימות מהאפליקציה"
                  value={formData.totpCode}
                  onChange={(e) => setFormData({ ...formData, totpCode: e.target.value })}
                  required={requiresTwoFactor}
                  disabled={isLoading}
                  maxLength={6}
                  minLength={6}
                  autoFocus
                />
              </div>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={
                isLoading ||
                (!requiresTwoFactor && turnstileStatus !== 'success') ||
                (requiresTwoFactor && !formData.totpCode)
              }
            >
              {isLoading ? 'מתחבר...' : requiresTwoFactor ? 'אמת קוד' : 'התחבר'}
            </Button>
            {!requiresTwoFactor && (
              <div className="space-y-2 mb-4">
                <Label htmlFor="turnstile" className="text-center block">
                  אימות אבטחה
                </Label>
                <div className="flex justify-center w-full mt-2">
                  <TurnstileCaptcha
                    ref={turnstileRef}
                    onVerify={handleTurnstileVerify}
                    onError={handleTurnstileError}
                    onExpire={handleTurnstileExpire}
                  />
                </div>
              </div>
            )}
          </form>
        </CardContent>
        <CardFooter className="flex flex-col space-y-4">
          <div className="text-sm text-center text-muted-foreground">
            אין לך חשבון? צור קשר עם מנהל המערכת
          </div>
        </CardFooter>
      </Card>
    </div>
  )
}

// Main component that wraps the content in a Suspense boundary
export default function SignIn() {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center min-h-[calc(100vh-8rem)] py-8 px-4">
          Loading...
        </div>
      }
    >
      <SignInContent />
    </Suspense>
  )
}
